import { useRef, useState } from "react";
import clsx from "clsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass } from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import { WorkbenchSearchInput } from "@/components/ui/workbench";
import { useDeferredPointerHover } from "./useDeferredPointerHover";
import { guardiansToolbarIconButtonClass } from "./guardiansToolbarButton";

const SEARCH_HOVER_CLOSE_MS = 450;
const SEARCH_PANEL_WIDTH_CLASS = "w-64";

export interface GuardiansExpandableSearchProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

/** Hover-to-expand search field anchored on the toolbar search icon. */
export function GuardiansExpandableSearch({
  value,
  onChange,
  placeholder,
}: GuardiansExpandableSearchProps) {
  const { cancelClose, scheduleClose } = useDeferredPointerHover(SEARCH_HOVER_CLOSE_MS);
  const [open, setOpen] = useState(false);
  const inputFocusedRef = useRef(false);

  const openSearch = () => {
    cancelClose();
    setOpen(true);
  };

  const tryClose = () => {
    scheduleClose(() => {
      if (!inputFocusedRef.current) setOpen(false);
    });
  };

  return (
    <div
      className="relative flex shrink-0 items-center"
      onPointerEnter={openSearch}
      onPointerLeave={tryClose}
      data-testid="guardians-search"
      data-toolbar-fixed
    >
      <div
        data-testid="guardians-search-panel"
        className={clsx(
          "absolute right-full top-1/2 z-30 mr-2 -translate-y-1/2 origin-right",
          "overflow-hidden transition-[width,opacity] duration-200 ease-out",
          open
            ? clsx("pointer-events-auto opacity-100", SEARCH_PANEL_WIDTH_CLASS)
            : "pointer-events-none w-0 opacity-0"
        )}
        aria-hidden={!open}
      >
        <div
          className={clsx(
            SEARCH_PANEL_WIDTH_CLASS,
            "shrink-0 rounded-xl border border-slate-500 bg-slate-800",
            "shadow-[0_8px_24px_rgba(0,0,0,0.45)] ring-1 ring-white/10"
          )}
        >
          <WorkbenchSearchInput
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            embedded
            showLeadingIcon
            className="h-9"
            data-testid="guardians-search-input"
            onFocus={() => {
              cancelClose();
              inputFocusedRef.current = true;
            }}
            onBlur={() => {
              inputFocusedRef.current = false;
              tryClose();
            }}
          />
        </div>
      </div>
      <button
        type="button"
        aria-label="Search"
        data-testid="guardians-search-trigger"
        className={clsx("relative z-40", guardiansToolbarIconButtonClass)}
      >
        <FontAwesomeIcon icon={faMagnifyingGlass} />
      </button>
    </div>
  );
}
